/** IdeaRoom webhook / Zapier / paste → order-intake fields. Shape from a real ShedCor payload. */

import {
  asIdeaRoomRecord,
  coerceIdeaRoomPayload,
  ideaRoomFloorPlanUrl,
  ideaRoomHashFromUrl,
  ideaRoomStr,
} from "./ideaRoomExportParse.js";

export type IdeaRoomExportKind = "customer" | "inventory";
export type IdeaRoomExportSalePath = "inventory_no_sale_yet" | "cash" | "finance" | "rto";

export type IdeaRoomExportTranslation = {
  external_source_id: string;
  kind: IdeaRoomExportKind;
  proposed_sale_path: IdeaRoomExportSalePath | null;
  proposed_options: Record<string, unknown>;
  customer_display_name: string;
};

export {
  coerceIdeaRoomPayload,
  ideaRoomFloorPlanUrl,
  ideaRoomNotes,
  ideaRoomRawPayload,
  parseIdeaRoomExportJson,
} from "./ideaRoomExportParse.js";

const LINE_PRODUCT_KEYS: Record<string, string> = {
  size: "size",
  "wall-height": "wall_height",
  "siding-color": "siding_color",
  "trim-color": "trim_color",
  "roof-color": "roof_color",
  "roof-material": "roof_material",
};

function unwrap(payload: unknown): Record<string, unknown> | null {
  const coerced = coerceIdeaRoomPayload(payload);
  const root = asIdeaRoomRecord(coerced);
  if (!root) return null;
  const nested =
    asIdeaRoomRecord(root.payload) ??
    asIdeaRoomRecord(root.data) ??
    asIdeaRoomRecord(root.order) ??
    asIdeaRoomRecord(root.quote);
  return nested ? { ...nested, ...root } : root;
}

function ideaRoomOrder(root: Record<string, unknown>): Record<string, unknown> {
  return asIdeaRoomRecord(root.order) ?? root;
}

function firstStr(root: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const direct = ideaRoomStr(root[key]);
    if (direct) return direct;
    const nested = asIdeaRoomRecord(root[key]);
    if (nested) {
      const id =
        ideaRoomStr(nested.hash) ||
        ideaRoomStr(nested.uuid) ||
        ideaRoomStr(nested.id) ||
        ideaRoomStr(nested.order_id) ||
        ideaRoomStr(nested.orderId) ||
        ideaRoomStr(nested.orderNumber) ||
        ideaRoomStr(nested.order_number) ||
        ideaRoomStr(nested.job_id) ||
        ideaRoomStr(nested.jobId);
      if (id) return id;
    }
  }
  return "";
}

function externalSourceId(root: Record<string, unknown>): string {
  const order = ideaRoomOrder(root);
  return (
    ideaRoomStr(root.hash) ||
    ideaRoomStr(order.hash) ||
    ideaRoomHashFromUrl(ideaRoomStr(order.url) || ideaRoomStr(root.url)) ||
    ideaRoomStr(order.uuid) ||
    ideaRoomStr(root.uuid) ||
    firstStr(root, [
      "idearoom_order_id",
      "order_id",
      "orderId",
      "order_number",
      "orderNumber",
      "job_id",
      "jobId",
      "quote_id",
      "quoteId",
      "id",
    ])
  );
}

function customerName(root: Record<string, unknown>): string {
  const order = ideaRoomOrder(root);
  const customer =
    asIdeaRoomRecord(root.customer) ??
    asIdeaRoomRecord(order.customer) ??
    asIdeaRoomRecord(root.buyer) ??
    asIdeaRoomRecord(root.client) ??
    asIdeaRoomRecord(root.contact);
  const fromObj = customer
    ? ideaRoomStr(customer.name) ||
      [
        ideaRoomStr(customer.first_name) || ideaRoomStr(customer.firstName),
        ideaRoomStr(customer.last_name) || ideaRoomStr(customer.lastName),
      ]
        .filter(Boolean)
        .join(" ")
    : "";
  return (
    fromObj ||
    ideaRoomStr(root.customer_name) ||
    ideaRoomStr(root.customerName) ||
    ideaRoomStr(root.buyer_name) ||
    ideaRoomStr(root.client_name)
  );
}

function inferKind(root: Record<string, unknown>): IdeaRoomExportKind {
  const raw = (
    ideaRoomStr(root.kind) ||
    ideaRoomStr(root.order_type) ||
    ideaRoomStr(root.orderType) ||
    ideaRoomStr(root.inventory_type)
  ).toLowerCase();
  if (raw.includes("inventory") || raw.includes("stock") || raw === "build") return "inventory";
  return "customer";
}

function inferSalePath(root: Record<string, unknown>, kind: IdeaRoomExportKind): IdeaRoomExportSalePath | null {
  if (kind === "inventory") return "inventory_no_sale_yet";
  const order = ideaRoomOrder(root);
  if (root.isRentToOwn === true || order.isRentToOwn === true) return "rto";
  const raw = (
    ideaRoomStr(root.sale_path) ||
    ideaRoomStr(root.salePath) ||
    ideaRoomStr(root.payment_type) ||
    ideaRoomStr(root.paymentType) ||
    ideaRoomStr(root.financing) ||
    ideaRoomStr(order.depositType)
  ).toLowerCase();
  if (raw.includes("rto") || raw.includes("rent")) return "rto";
  if (raw.includes("finance") || raw.includes("loan")) return "finance";
  if (raw.includes("cash")) return "cash";
  return null;
}

function lineItemRecords(order: Record<string, unknown>): Record<string, unknown>[] {
  if (!Array.isArray(order.lineItems)) return [];
  const out: Record<string, unknown>[] = [];
  for (const item of order.lineItems) {
    const rec = asIdeaRoomRecord(item);
    if (rec) out.push(rec);
  }
  return out;
}

function lineItemLabel(item: Record<string, unknown>): string {
  const value = ideaRoomStr(item.value) || ideaRoomStr(item.model);
  if (value) return value;
  const desc = ideaRoomStr(item.description);
  const colon = desc.indexOf(": ");
  return colon > 0 ? desc.slice(colon + 2).trim() : desc;
}

function compactBuildingSpec(order: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const ip = asIdeaRoomRecord(order.integrationProperties);
  const styleFromIp = ip ? ideaRoomStr(ip.buildingStyle) : "";
  if (styleFromIp) out.style = styleFromIp;
  const size = ip ? asIdeaRoomRecord(ip.buildingSize) : null;
  if (size) {
    if (ideaRoomStr(size.width)) out.width = ideaRoomStr(size.width);
    if (ideaRoomStr(size.length)) out.length = ideaRoomStr(size.length);
    if (ideaRoomStr(size.height)) out.height = ideaRoomStr(size.height);
  }
  const items = lineItemRecords(order);
  for (const [productKey, optKey] of Object.entries(LINE_PRODUCT_KEYS)) {
    if (out[optKey]) continue;
    const item = items.find((row) => ideaRoomStr(row.productKey) === productKey);
    if (!item) continue;
    const desc = lineItemLabel(item);
    if (desc) out[optKey] = desc;
  }
  if (!out.style) {
    const item = items.find((row) => ideaRoomStr(row.productKey) === "style");
    if (item) out.style = lineItemLabel(item);
  }
  const skuItem = items.find((row) => ideaRoomStr(row.sku));
  if (skuItem) out.sku = ideaRoomStr(skuItem.sku);
  const supplier = ideaRoomStr(order.supplier) || ideaRoomStr(order.supplierLabel);
  if (supplier) out.supplier = supplier;
  if (typeof order.totalPrice === "number") out.total_price = order.totalPrice;
  const url = ideaRoomStr(order.url);
  if (url) out.configurator_url = url;
  const sales = asIdeaRoomRecord(order.salesAccount) ?? asIdeaRoomRecord(order.dealer);
  const salesEmail = sales ? ideaRoomStr(sales.email) : "";
  if (salesEmail) out.sales_account_email = salesEmail;
  const ship = asIdeaRoomRecord(order.shippingAddress);
  if (ship) {
    if (ideaRoomStr(ship.state)) out.ship_state = ideaRoomStr(ship.state);
    if (ideaRoomStr(ship.zip)) out.ship_zip = ideaRoomStr(ship.zip);
  }
  const line_items: Array<Record<string, unknown>> = [];
  for (const item of items) {
    const description = ideaRoomStr(item.description);
    if (!description) continue;
    const row: Record<string, unknown> = { description };
    const optionKey = ideaRoomStr(item.optionKey);
    if (optionKey) row.option_key = optionKey;
    const productKey = ideaRoomStr(item.productKey);
    if (productKey) row.product_key = productKey;
    if (typeof item.price === "number") row.price = item.price;
    if (typeof item.quantity === "number") row.quantity = item.quantity;
    line_items.push(row);
  }
  if (line_items.length) out.line_items = line_items;
  return out;
}

function optionsFrom(root: Record<string, unknown>): Record<string, unknown> {
  const pasted = asIdeaRoomRecord(root.options) ?? asIdeaRoomRecord(root.configuration) ?? asIdeaRoomRecord(root.features);
  const out: Record<string, unknown> = pasted ? { ...pasted } : {};
  Object.assign(out, compactBuildingSpec(ideaRoomOrder(root)));
  const floor = ideaRoomFloorPlanUrl(root);
  if (floor) out.floor_plan_url = floor;
  const eventType = ideaRoomStr(root.eventType);
  if (eventType) out.event_type = eventType;
  const status = ideaRoomStr(ideaRoomOrder(root).status);
  if (status) out.order_status = status;
  return out;
}

export function translateIdeaRoomExport(
  payload: unknown
): { ok: true; translation: IdeaRoomExportTranslation } | { ok: false; error: string } {
  const root = unwrap(payload);
  if (!root) return { ok: false, error: "IdeaRoom export must be a JSON object." };

  const external_source_id = externalSourceId(root);
  if (!external_source_id) {
    return {
      ok: false,
      error: "Could not find an IdeaRoom id (hash, uuid, idearoom_order_id, orderNumber, or id).",
    };
  }

  const kind = inferKind(root);
  const name = customerName(root);
  const proposed_options = optionsFrom(root);
  if (name) proposed_options.customer_name = name;

  return {
    ok: true,
    translation: {
      external_source_id,
      kind,
      proposed_sale_path: inferSalePath(root, kind),
      proposed_options,
      customer_display_name: name,
    },
  };
}
